import { IAudioSoundFxPort, AudioConfigDTO, VoiceBindingDTO } from '../ports/types';
import { AudioMixer, AppEdition } from './AudioMixer';
import { PresetSoundSynthesizer, PresetSoundType } from './PresetSoundSynthesizer';
import { CustomVoiceManager, VoiceActionType, VoiceSlotSummaryDTO } from './CustomVoiceManager';
import { PhysicsEventHandler } from './PhysicsEventHandler';

export interface AudioSoundFxServiceOptions {
  edition?: AppEdition;
  audioContext?: AudioContext | null;
  initialConfig?: Partial<AudioConfigDTO>;
}

/**
 * オーディオ演出ユニット統合ファサードサービス (AudioSoundFxService)
 * IAudioSoundFxPort を完全実装し、AudioMixer, PresetSoundSynthesizer,
 * CustomVoiceManager, PhysicsEventHandler を協調動作させる。
 */
export class AudioSoundFxService implements IAudioSoundFxPort {
  private mixer: AudioMixer;
  private presetSynthesizer: PresetSoundSynthesizer;
  private customVoiceManager: CustomVoiceManager;
  private physicsEventHandler: PhysicsEventHandler;

  constructor(options?: AudioSoundFxServiceOptions) {
    this.mixer = new AudioMixer({
      edition: options?.edition ?? 'pc_app_paid',
      audioContext: options?.audioContext,
      initialConfig: options?.initialConfig,
    });

    this.presetSynthesizer = new PresetSoundSynthesizer({
      mixer: this.mixer,
    });

    this.customVoiceManager = new CustomVoiceManager({
      mixer: this.mixer,
    });

    this.physicsEventHandler = new PhysicsEventHandler({
      mixer: this.mixer,
      presetSynthesizer: this.presetSynthesizer,
      customVoiceManager: this.customVoiceManager,
    });
  }

  // ==========================================
  // IAudioSoundFxPort 実装
  // ==========================================

  /**
   * プリセット効果音（ぷるん♪、ぽよん、伸び、水滴等）を再生
   */
  public playPresetSE(soundType: 'plump' | 'bounce' | 'stretch' | 'water', volumeScale: number = 1.0): void {
    this.presetSynthesizer.playPresetSE(soundType as PresetSoundType, volumeScale);
  }

  /**
   * 手持ちボイスをアクションに紐付け登録
   */
  public async bindCustomVoice(dto: VoiceBindingDTO): Promise<void> {
    const result = await this.customVoiceManager.bindVoice(dto);
    if (!result.success) {
      throw new Error(`[AudioSoundFxService] Failed to bind custom voice: ${result.errorMessage}`);
    }
  }

  /**
   * 音量設定等の更新
   */
  public updateConfig(dto: AudioConfigDTO): void {
    this.mixer.updateConfig(dto);
  }

  /**
   * 物理ドメインイベントの直接ハンドラ (ACL翻訳・スロットリング・SE/ボイス発音)
   */
  public handlePhysicsEvent(event: { type: string; intensity: number }): void {
    this.physicsEventHandler.handlePhysicsEvent(event);
  }

  // ==========================================
  // 拡張補助メソッド
  // ==========================================

  public getConfig(): AudioConfigDTO {
    return this.mixer.getConfig();
  }

  public getSlotsSummary(): VoiceSlotSummaryDTO[] {
    return this.customVoiceManager.getSlotsSummary();
  }

  public unbindCustomVoice(actionType: VoiceActionType): void {
    this.customVoiceManager.unbindVoice(actionType);
  }

  public playCustomVoice(actionType: VoiceActionType): boolean {
    return this.customVoiceManager.playVoice(actionType);
  }

  public stopCustomVoice(): void {
    this.customVoiceManager.stopVoice();
  }

  public stopAllSE(): void {
    this.presetSynthesizer.stopAllSE();
  }

  public setEdition(edition: AppEdition): void {
    this.mixer.setEdition(edition);
  }

  public getEdition(): AppEdition {
    return this.mixer.getEdition();
  }

  public async ensureRunning(): Promise<void> {
    await this.mixer.ensureRunning();
  }

  // 内部コンポーネントへのアクセサ
  public getMixer(): AudioMixer {
    return this.mixer;
  }

  public getPresetSynthesizer(): PresetSoundSynthesizer {
    return this.presetSynthesizer;
  }

  public getCustomVoiceManager(): CustomVoiceManager {
    return this.customVoiceManager;
  }

  public getPhysicsEventHandler(): PhysicsEventHandler {
    return this.physicsEventHandler;
  }
}
